import React, { useState } from 'react';
import {
  useCreateNationalCode,
  useGetJurisdictions,
  useSelectJurisdiction,
  useUpdateJurisdiction,
  useUpdateNationalCode
} from '../services/jurisdiction.service';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { cloneDeep } from 'lodash';
// @ts-expect-error untyped JS service
import { useGetWorkflowById } from '../services/workflow.services';
// @ts-expect-error untyped JS const map
import { REPORT_TYPES } from '../consts/reportTypes';

interface NationalCodeFund {
  id?: string;
  key: string;
  code: string;
  isIncluded: boolean;
  isCustomMasterFund: boolean;
  baseCurrency?: string;
  masterFeederStatus?: string;
  masterFundIds?: string[];
  aifNoReportingFlag?: boolean;
  isLastReporting?: boolean;
}

interface JurisdictionValue {
  id?: string;
  aifm: string;
  country: { reporting_member_state?: string } | string;
  funds: NationalCodeFund[];
}

interface UseJurisdictionParams {
  onClose: () => void;
  periodId: string;
  projectId: string;
  refetch?: () => void;
  enabledFetch: boolean;
  isRedirectToExtraction?: boolean;
}

type Jurisdiction = Record<string, any>;

interface UseJurisdictionReturn {
  jurisdictions: Jurisdiction[];
  isLoadingJurisdictionMutate: boolean;
  setIsLoadingJurisdictionMutate: (value: boolean) => void;
  refetchJurisdictions: () => void;
  handleJurisdictionSubmit: (e: React.FormEvent, values: JurisdictionValue[]) => Promise<void>;
}

export default function useJurisdiction({
  onClose,
  periodId,
  projectId,
  refetch,
  enabledFetch,
  isRedirectToExtraction = true
}: UseJurisdictionParams): UseJurisdictionReturn {
  const navigate = useNavigate();

  const { data: jurisdictions, refetch: refetchJurisdictions } = useGetJurisdictions({
    periodId,
    queryParams: {
      enabled: enabledFetch,
      onSuccess: (res: Jurisdiction[]) => {
        return cloneDeep(res);
      }
    }
  });

  const { data: project } = useGetWorkflowById({
    id: projectId,
    queryParams: { enabled: !!projectId }
  }) as { data?: { report_type?: string } };

  const [isLoadingJurisdictionMutate, setIsLoadingJurisdictionMutate] = useState<boolean>(false);

  const jurisdictionHasBeenCreatedAlready = (jurisdictions as Jurisdiction[])?.length > 0;

  const selectJurisdiction = useSelectJurisdiction();
  const updateJurisdiction = useUpdateJurisdiction();
  const createNationalCode = useCreateNationalCode();
  const updateNationalCode = useUpdateNationalCode();

  const handleCreateNationalCode = async (jurisdictionId: string, funds: NationalCodeFund[]) => {
    const newNatCodes = funds
      .filter(fund => !fund.id)
      .map(fund => ({
        fund_id: fund.key,
        aif_national_code: fund.code,
        is_included: fund.isIncluded,
        is_custom: fund.isCustomMasterFund,
        base_currency: fund.baseCurrency,
        aif_master_feeder_status: fund.masterFeederStatus,
        master_fund_ids: fund.masterFundIds || [],
        aif_no_reporting_flag: fund.aifNoReportingFlag,
        last_reporting_flag: fund.isLastReporting
      }));

    const oldNatCodes = funds
      .filter(fund => fund.id)
      .map(fund => ({
        fund_id: fund.key,
        aif_national_code: fund.code,
        national_code_id: fund.id,
        is_custom: fund.isCustomMasterFund,
        is_included: fund.isIncluded,
        base_currency: fund.baseCurrency,
        aif_master_feeder_status: fund.masterFeederStatus,
        master_fund_ids: fund.masterFundIds || [],
        aif_no_reporting_flag: fund.aifNoReportingFlag,
        last_reporting_flag: fund.isLastReporting
      }));

    const requests: Promise<unknown>[] = [];

    if (newNatCodes.length > 0)
      requests.push(
        createNationalCode.mutateAsync({
          jurisdictionId,
          data: newNatCodes
        })
      );
    if (oldNatCodes.length > 0)
      requests.push(
        updateNationalCode.mutateAsync({
          nationalCodeId: jurisdictionId,
          data: oldNatCodes
        })
      );

    return Promise.all(requests);
  };

  const handleJurisdictionSubmit = async (e: React.FormEvent, values: JurisdictionValue[]) => {
    e.preventDefault();

    let jurisdictionUpdateError: any = null;

    let jurisdictionCreateError: any = null;

    try {
      setIsLoadingJurisdictionMutate(true);

      const mutationPromises = [...values]?.map(async juris => {
        if (!juris.aifm) throw new Error('Please select the AIFM national code first.');
        if (!(
          (juris?.country as { reporting_member_state?: string })?.reporting_member_state ||
          juris?.country
        ))
          throw new Error('Please select the jurisdiction country first.');

        if (juris.id) {
          const jurisRes: any = await updateJurisdiction.mutateAsync(
            {
              id: juris.id,
              data: {
                reporting_member_state:
                  (juris.country as { reporting_member_state?: string }).reporting_member_state ||
                  juris.country,
                aifm_national_code: juris.aifm
              }
            },
            {
              onError: (err: unknown) => {
                jurisdictionUpdateError = err;
              }
            }
          );

          return handleCreateNationalCode(jurisRes.id, juris.funds);
        } else {
          const jurisRes: any = await selectJurisdiction.mutateAsync(
            {
              periodId,
              data: {
                reporting_member_state:
                  (juris.country as { reporting_member_state?: string }).reporting_member_state ||
                  juris.country,
                aifm_national_code: juris.aifm
              }
            },
            {
              onError: (err: unknown) => {
                jurisdictionCreateError = err;
              }
            }
          );

          return handleCreateNationalCode(jurisRes.id, juris.funds);
        }
      });

      await Promise.all(mutationPromises);

      setIsLoadingJurisdictionMutate(false);

      if (project?.report_type === REPORT_TYPES.CONVERSION) {
        // Conversion has no extraction / source-files phase — every successful
        // jurisdiction save (first or subsequent) returns the user to the
        // workflow's quarters page rather than advancing through the stepper.
        if (refetch) refetch?.();
        refetchJurisdictions();
        toast.success('Jursdictions have been updated successful');
        navigate(`/project/quaters/${projectId}`);
      } else if (!jurisdictionHasBeenCreatedAlready && isRedirectToExtraction) {
        // First-create onboarding redirect now points at Source Files —
        // the next stepper stage after Jurisdictions. Previously this
        // jumped two stages ahead to Extraction, skipping the user past
        // the file-upload step they still need to complete.
        navigate(`/project/quaters/${projectId}/${periodId}/upload-files`);
      } else {
        if (refetch) refetch?.();
        refetchJurisdictions();
        onClose();
        toast.success('Jursdictions have been updated successful');
      }
    } catch (err) {
      if (jurisdictionCreateError) {
        toast.error(`Error while creating jurisdiction ${jurisdictionCreateError?.data?.detail}`);
      } else if (jurisdictionUpdateError) {
        toast.error(`Error while updating jurisdiction ${jurisdictionUpdateError?.data?.detail}`);
      } else {
        toast.error(
          `Unexpected error happened while updating jurisdiction ${(err as any)?.data?.detail || err}`
        );
        console.log(`useJurisdiction::handleJurisdictionSubmit`, err);
      }
      setIsLoadingJurisdictionMutate(false);
    }
  };

  return {
    jurisdictions: (jurisdictions as Jurisdiction[]) || [],
    isLoadingJurisdictionMutate,
    setIsLoadingJurisdictionMutate,
    refetchJurisdictions,
    handleJurisdictionSubmit
  };
}
